import {
	IconArrowRight,
	IconBriefcase,
	IconClock,
	IconCode,
	IconDeviceGamepad2,
	IconFolder,
	IconHeart,
	IconHome,
	IconMail,
	IconNotebook,
	IconServer,
} from "@tabler/icons-react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { allPosts } from "content-collections";
import { type CSSProperties, type ReactNode, useEffect } from "react";
import { NewsletterSignup } from "@/components/misc/NewsletterSignup";
import { Button } from "@/components/ui/button";
import {
	HoverCard,
	HoverCardContent,
	HoverCardTrigger,
} from "@/components/ui/hover-card";
import { config } from "@/data/config";
import { cn } from "@/lib/utils";
import { schema, toScript } from "@/utils/schema";
import { seo } from "@/utils/seo";

export const Route = createFileRoute("/_pages/")({
	component: RouteComponent,
	head: () => {
		const base = seo({
			title: config.Name,
			description:
				"Software engineer specializing in email infrastructure and deliverability. Early Klaviyo employee, builder of side projects, based in Massachusetts.",
			keywords:
				"francis baker, software engineer, email infrastructure, email deliverability, MTA, ESP, cloud email, developer experience",
			url: config.Url,
		});
		return {
			...base,
			scripts: [
				...base.scripts,
				toScript(schema.profilePage(config.Url)),
				toScript(schema.breadcrumb([{ name: config.Name, url: config.Url }])),
			],
		};
	},
});

function DogHoverCard({
	name,
	breed,
	imageSrc,
	age,
}: {
	name: string;
	breed: string;
	imageSrc: string;
	age: number;
}) {
	return (
		<HoverCard>
			<HoverCardTrigger className="font-medium text-foreground underline decoration-border underline-offset-4 transition-colors hover:text-primary">
				{name} ({breed})
			</HoverCardTrigger>
			<HoverCardContent className="w-80 overflow-hidden p-0">
				<img src={imageSrc} alt={name} className="size-full object-contain" />
				<div className="p-4">
					<p className="text-sm text-muted-foreground">
						{name} is {age} years old.
					</p>
				</div>
			</HoverCardContent>
		</HoverCard>
	);
}

function NestedPanel({
	children,
	className,
}: {
	children: ReactNode;
	className?: string;
}) {
	return (
		<div
			className={cn(
				"flex flex-col rounded-lg border border-border/60 bg-muted/25 p-5 dark:bg-muted/10 sm:p-6",
				className,
			)}
		>
			{children}
		</div>
	);
}

function SectionPanel({
	children,
	className,
	animationDelay,
}: {
	children: ReactNode;
	className?: string;
	animationDelay?: string;
}) {
	return (
		<div
			className={cn(
				"animate-in rounded-xl border border-border/70 bg-card p-6 duration-800 fill-mode-both fade-in slide-in-from-bottom-8 dark:inset-ring dark:inset-ring-white/5 sm:p-8",
				className,
			)}
			style={
				animationDelay
					? ({ animationDelay } satisfies CSSProperties)
					: undefined
			}
		>
			{children}
		</div>
	);
}

function RouteComponent() {
	const julesAge = Math.floor(
		(Date.now() - new Date("2021-08-18").getTime()) /
			(1000 * 60 * 60 * 24 * 365.25),
	);
	const haroldAge = Math.floor(
		(Date.now() - new Date("2023-10-15").getTime()) /
			(1000 * 60 * 60 * 24 * 365.25),
	);

	useEffect(() => {
		const preloadImages = ["/images/jules.png", "/images/harold.jpg"];
		for (const src of preloadImages) {
			const img = new Image();
			img.src = src;
		}
	}, []);

	const currentBlogPosts = allPosts
		.filter((post) => post.published)
		.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

	const latest = currentBlogPosts[0];
	const showNewsletter = config.ShowNewsletter;
	const showLatestCard = Boolean(latest);

	return (
		<div className="relative isolate overflow-hidden bg-background">
			<div
				className="surface-grid pointer-events-none absolute inset-0 opacity-50"
				aria-hidden
			/>
			<div
				className="pointer-events-none absolute -top-32 left-1/2 h-112 w-[min(56rem,100%)] -translate-x-1/2 rounded-full bg-linear-to-b from-primary/15 via-transparent to-transparent blur-3xl dark:hidden"
				aria-hidden
			/>

			<main className="relative pt-12 pb-20 sm:pt-16">
				<div className="mx-auto max-w-6xl px-4 lg:px-8">
					<section
						aria-label="Introduction"
						className="grid gap-12 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center lg:gap-16"
					>
						<div className="order-2 min-w-0 lg:order-1">
							<p
								className="animate-in text-sm font-medium text-primary duration-700 fill-mode-both fade-in slide-in-from-bottom-4"
								style={{ animationDelay: "0.05s" }}
							>
								Software engineer · Email &amp; infrastructure
							</p>
							<h1
								className="mt-3 max-w-[24ch] animate-in text-4xl font-semibold tracking-tight text-balance text-foreground duration-800 fill-mode-both fade-in slide-in-from-bottom-8 sm:text-5xl lg:text-6xl"
								style={{ animationDelay: "0.12s" }}
							>
								Hi, I&apos;m <span className="gradient-text">Francis</span>{" "}
								<span className="inline-block animate-wave" aria-hidden>
									👋
								</span>
							</h1>
							<p
								className="mt-6 max-w-[56ch] animate-in text-base text-pretty text-muted-foreground duration-800 fill-mode-both fade-in slide-in-from-bottom-8 sm:text-lg"
								style={{ animationDelay: "0.2s" }}
							>
								I build reliable email systems, from deliverability and
								compliance operations to the engineering behind large-scale
								sending pipelines. Cloud ESPs, self-hosted MTAs, and the tooling
								around them.
							</p>

							<div
								className="mt-8 flex animate-in flex-wrap gap-3 duration-700 fill-mode-both fade-in slide-in-from-bottom-8"
								style={{ animationDelay: "0.28s" }}
							>
								<Button asChild size="lg" className="rounded-full px-6">
									<Link to="/blog">
										<IconNotebook className="size-4" />
										Read the blog
									</Link>
								</Button>
								<Button
									asChild
									variant="outline"
									size="lg"
									className="rounded-full border-border/80 bg-background/50 px-6 backdrop-blur-sm"
								>
									<Link to="/projects">
										<IconFolder className="size-4" />
										Projects
									</Link>
								</Button>
								<Button
									asChild
									variant="ghost"
									size="lg"
									className="rounded-full text-muted-foreground hover:text-foreground"
								>
									<Link to="/contact">
										<IconMail className="size-4" />
										Contact
									</Link>
								</Button>
							</div>
						</div>

						<div
							className="order-1 flex animate-in justify-center duration-800 fill-mode-both fade-in slide-in-from-bottom-8 lg:order-2 lg:shrink-0"
							style={{ animationDelay: "0.15s" }}
						>
							<img
								src="/images/avatar.png"
								alt="Francis Baker headshot"
								className="size-64 rounded-2xl object-cover outline-1 -outline-offset-1 outline-foreground/10 sm:size-72 lg:size-80 dark:outline-white/10"
							/>
						</div>
					</section>

					{(showNewsletter || showLatestCard) && (
						<section
							aria-label="Latest content"
							className={cn(
								"mt-12 sm:mt-14",
								showNewsletter &&
									showLatestCard &&
									"grid grid-cols-1 gap-6 lg:grid-cols-2 lg:items-stretch lg:gap-8",
							)}
						>
							{showNewsletter && (
								<div
									className="h-full min-h-0 min-w-0 animate-in duration-800 fill-mode-both fade-in slide-in-from-bottom-8"
									style={{ animationDelay: "0.38s" }}
								>
									<NewsletterSignup className="h-full min-h-0 min-w-0" />
								</div>
							)}

							{latest && (
								<article
									className="group relative flex h-full min-h-0 min-w-0 animate-in overflow-hidden rounded-xl border border-border/70 bg-card duration-800 fill-mode-both fade-in slide-in-from-bottom-8 dark:inset-ring dark:inset-ring-white/5"
									style={{
										animationDelay: showNewsletter ? "0.46s" : "0.38s",
									}}
								>
									<Link
										to="/blog/$path"
										params={{ path: latest._meta.path }}
										className="flex min-h-0 min-w-0 flex-1 flex-col gap-3 p-6 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background focus-visible:outline-none sm:p-8"
									>
										<p className="text-sm font-medium text-muted-foreground">
											Latest post
										</p>
										<div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
											{latest.date && (
												<span className="inline-flex items-center gap-1.5">
													<IconClock
														className="size-4 shrink-0 text-primary/80"
														aria-hidden
													/>
													{new Date(latest.date).toLocaleDateString("en-US", {
														year: "numeric",
														month: "short",
														day: "numeric",
													})}
												</span>
											)}
											<span className="rounded-full bg-muted/80 px-2.5 py-0.5 font-medium text-foreground tabular-nums">
												{latest.readingTime}
											</span>
										</div>
										<h2 className="max-w-[40ch] text-xl font-semibold tracking-tight text-balance text-foreground transition-colors group-hover:text-primary sm:text-2xl">
											{latest.title}
										</h2>
										{latest.summary ? (
											<p className="line-clamp-2 max-w-[56ch] text-base text-pretty text-muted-foreground">
												{latest.summary}
											</p>
										) : null}
										<div className="mt-auto flex items-center gap-1.5 pt-2 text-sm font-medium text-primary">
											<span>Read article</span>
											<IconArrowRight
												className="size-3.5 shrink-0 transition-transform group-hover:translate-x-0.5"
												aria-hidden
											/>
										</div>
									</Link>
								</article>
							)}
						</section>
					)}

					<section
						aria-label="About me"
						className="mt-16 border-t border-border/60 pt-16 sm:mt-20 sm:pt-20"
					>
						<div className="grid gap-8">
							<div className="grid gap-8 lg:grid-cols-2">
								<SectionPanel animationDelay="0.58s">
									<div className="flex items-center gap-3">
										<span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
											<IconBriefcase className="size-5" aria-hidden />
										</span>
										<h2 className="text-xl font-semibold tracking-tight text-foreground">
											At Klaviyo
										</h2>
									</div>

									<dl className="@container/personal mt-6 grid gap-8">
										<div>
											<dt className="text-sm font-medium text-foreground">
												Deliverability &amp; Compliance
											</dt>
											<dd className="mt-2 text-base text-pretty text-muted-foreground">
												Early employee and first hire on{" "}
												<a
													href="https://www.klaviyo.com"
													className="font-medium text-foreground underline decoration-border underline-offset-4 hover:text-primary"
													rel="noopener noreferrer"
													target="_blank"
												>
													Klaviyo
												</a>
												&apos;s Deliverability &amp; Compliance team. Built the
												function, then moved into a hybrid ops and engineering
												role.
											</dd>
										</div>
										<div className="border-t border-border/60 pt-8">
											<dt className="flex items-center gap-2 text-sm font-medium text-foreground">
												<IconServer
													className="size-4 shrink-0 text-primary"
													aria-hidden
												/>
												Today
											</dt>
											<dd className="mt-2 text-base text-pretty text-muted-foreground">
												Senior engineer on the internal MTA pipeline—traffic
												routing, IP warming, and the systems that moved tens of
												thousands of brands onto mail we run ourselves.
											</dd>
										</div>
									</dl>
								</SectionPanel>

								<SectionPanel animationDelay="0.66s">
									<div className="flex items-center gap-3">
										<span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
											<IconCode className="size-5" aria-hidden />
										</span>
										<h2 className="text-xl font-semibold tracking-tight text-foreground">
											Side projects
										</h2>
									</div>

									<p className="mt-6 text-base text-pretty text-muted-foreground">
										<a
											href="https://emailguard.co"
											className="font-medium text-foreground underline decoration-border underline-offset-4 hover:text-primary"
											rel="noopener noreferrer"
											target="_blank"
										>
											EmailGuard
										</a>
										,{" "}
										<a
											href="https://github.com/baker/inboundparse"
											className="font-medium text-foreground underline decoration-border underline-offset-4 hover:text-primary"
											rel="noopener noreferrer"
											target="_blank"
										>
											InboundParse
										</a>
										,{" "}
										<a
											href="https://ineedatest.email"
											className="font-medium text-foreground underline decoration-border underline-offset-4 hover:text-primary"
											rel="noopener noreferrer"
											target="_blank"
										>
											ineedatest.email
										</a>
										, and{" "}
										<a
											href="https://dnsbuddy.co"
											className="font-medium text-foreground underline decoration-border underline-offset-4 hover:text-primary"
											rel="noopener noreferrer"
											target="_blank"
										>
											DnsBuddy
										</a>
										—email validation, inbound parsing, disposable test inboxes,
										and DNS tooling, plus this site.
									</p>

									<Link
										to="/projects"
										className="mt-8 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:text-primary/90"
									>
										See what I&apos;ve built
										<IconArrowRight className="size-3.5 shrink-0" aria-hidden />
									</Link>
								</SectionPanel>
							</div>

							<SectionPanel animationDelay="0.74s">
								<div className="flex items-center gap-3">
									<span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
										<IconHeart className="size-5" aria-hidden />
									</span>
									<h2 className="text-xl font-semibold tracking-tight text-foreground">
										Outside of work
									</h2>
								</div>

								<div className="mt-6 grid gap-4 sm:gap-5 lg:grid-cols-2 lg:items-stretch lg:gap-6">
									<NestedPanel>
										<div className="flex items-center gap-2.5">
											<span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
												<IconHome className="size-4" aria-hidden />
											</span>
											<h3 className="text-sm font-medium text-foreground">
												Home &amp; family
											</h3>
										</div>
										<p className="mt-4 flex-1 text-base text-pretty text-muted-foreground">
											I live in Massachusetts with my wife and our two dogs,{" "}
											<DogHoverCard
												name="Jules"
												breed="pitbull"
												imageSrc="/images/jules.png"
												age={julesAge}
											/>{" "}
											and{" "}
											<DogHoverCard
												name="Harold"
												breed="golden retriever"
												imageSrc="/images/harold.jpg"
												age={haroldAge}
											/>
											.
										</p>
									</NestedPanel>

									<NestedPanel>
										<div className="flex items-center gap-2.5">
											<span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
												<IconDeviceGamepad2 className="size-4" aria-hidden />
											</span>
											<h3 className="text-sm font-medium text-foreground">
												Gaming
											</h3>
										</div>
										<p className="mt-4 flex-1 text-base text-pretty text-muted-foreground">
											When I&apos;m not outside, I&apos;m usually gaming. MMOs,
											FPS titles like{" "}
											<span className="font-medium text-foreground">
												Destiny 2
											</span>{" "}
											and{" "}
											<span className="font-medium text-foreground">
												Battlefield
											</span>
											, and whatever new loot grind or squad shooter friends are
											on that week.
										</p>
									</NestedPanel>
								</div>
							</SectionPanel>
						</div>
					</section>
				</div>
			</main>
		</div>
	);
}
